import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useLayoutEffect,
  useCallback,
} from 'react';
import { useWindowWidth } from '@react-hook/window-size';
import { formatBrandsBasedOnCategories } from 'components/Elements/BrandsLinks/BrandsLinks.helpers';
import LetterPicker, {
  SortingLetters,
} from 'components/Elements/LetterPicker/LetterPicker';
import Sticky from 'react-stickynode';
import pxToEm from 'theme/pxToEm';
import * as Styled from './LetterPickerContainer.styles';

import useBrands from '../../hooks/useBrands';

const LetterPickerContainer: React.FC = () => {
  const [stickyTop, setStickyTop] = useState(0);
  const [isLetterClicked, setIsLetterClicked] = useState(false);
  const [letterSelected, setLetterSelected] = useState<string>('');
  const stickyRef = useRef<HTMLDivElement>(null);
  const { filteredBrands } = useBrands();
  const windowWidth = useWindowWidth();
  const availableLetters = useMemo(() => {
    // @ts-expect-error // TODO: fix types
    const formatedBrands = formatBrandsBasedOnCategories(filteredBrands);
    return formatedBrands.map((brand: any) => brand[0]);
  }, [filteredBrands]);

  useEffect(() => {
    // After clicking letter - after 1 sec - enable back logic for sync scroll
    if (isLetterClicked) {
      setTimeout(() => setIsLetterClicked(false), 1000);
    }
  }, [isLetterClicked]);

  const handleClick = (value: string) => {
    // Hack to prevent onWindowScroll calcs, if letter was clicked
    setIsLetterClicked(true);
    setLetterSelected(value);
    const element = document.getElementById(value);
    const stickyElement = stickyRef.current;
    if (!stickyElement || !element) return;

    const stickyElementHeight = stickyElement.getBoundingClientRect().height;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition =
      elementPosition + window.pageYOffset - stickyElementHeight - stickyTop;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  const calcStickyTop = useCallback(() => {
    const announcement = document.getElementById('announcement');
    const header = document.getElementById('header');
    if (!header) return 0;
    const headerHeight = header.offsetHeight || 0;
    const announcementHeight = announcement?.offsetHeight || 0;
    return headerHeight + announcementHeight;
  }, []);

  useEffect(() => {
    const handleWindowScroll = () => {
      setStickyTop(calcStickyTop());
      if (isLetterClicked || windowWidth >= 768) {
        return;
      }
      const activeLetters = SortingLetters?.map((letter) => {
        const letterBox = document.getElementById(letter);
        if (letterBox != null) {
          return {
            letter,
            top: letterBox.getBoundingClientRect().top,
          };
        }
        return null;
      })?.filter((item) => {
        if (item == null) {
          return false;
        }
        return item.top < 300 && item.top > 0;
      });
      const activeLetter = activeLetters.pop();
      if (activeLetter != null && letterSelected !== activeLetter.letter) {
        setLetterSelected(activeLetter.letter);
      }
    };

    window.addEventListener('scroll', handleWindowScroll);
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [stickyTop, isLetterClicked, calcStickyTop, windowWidth, letterSelected]);

  useLayoutEffect(() => {
    setStickyTop(calcStickyTop());
  }, [calcStickyTop]);

  // Keep stickyTop in sync with dynamic header/announcement heights
  useEffect(() => {
    const header = document.getElementById('header');
    const announcement = document.getElementById('announcement');
    if (!header) return;

    const ro = new ResizeObserver(() => {
      const next = calcStickyTop();
      setStickyTop((prev) => (prev !== next ? next : prev));
    });

    ro.observe(header);
    if (announcement) ro.observe(announcement);

    // Also update on viewport resizes
    const handleResize = () => {
      const next = calcStickyTop();
      setStickyTop((prev) => (prev !== next ? next : prev));
    };
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [calcStickyTop]);

  const pickerActiveLetter = windowWidth < 768 ? letterSelected : null;

  return (
    <Sticky top={stickyTop} bottomBoundary="#bottomLine" innerZ={3}>
      <Styled.Wrapper
        paddingBottom={pxToEm(24)}
        paddingTop={pxToEm(24)}
        ref={stickyRef}
      >
        <LetterPicker
          // @ts-expect-error // TODO: fix types
          activeLetter={pickerActiveLetter}
          onLetterPressed={handleClick}
          letterSelected={letterSelected}
          availableLetters={availableLetters ?? []}
        />
      </Styled.Wrapper>
    </Sticky>
  );
};

export default LetterPickerContainer;
